import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  VerifiedUser as VerifiedUserIcon,
  WorkHistory as WorkHistoryIcon,
  Visibility as VisibilityIcon,
  Flag as FlagIcon,
  FormatQuote as FormatQuoteIcon,
} from '@mui/icons-material';
import NileshGuptaImg from '../assets/images/NileshGupta.jpeg';
import Seo from '../components/Seo';

const values = [
  {
    title: "Excellence",
    description: "We strive for excellence in every inspection and service we provide.",
    icon: <EmojiEventsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
  },
  {
    title: "Integrity",
    description: "We maintain the highest standards of professional integrity and ethics.",
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
  },
  {
    title: "Innovation",
    description: "We continuously innovate to provide better solutions for our clients.",
    icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
  }
];

const About = () => {
  return (
    <Container maxWidth="lg">
      <Seo
        title="About Us — Independent Inspection Agency"
        description="InspectIQ is an independent inspection and survey agency led by professionals with over a decade of QA/QC and commodity expertise, serving clients worldwide from bases in India."
        path="/about"
      />
      {/* Company Overview Section */}
      <Box sx={{ py: 8 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            mb: { xs: 2, md: 3 }
          }}
        >
          About InspectIQ
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          align="center" 
          sx={{ 
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            fontWeight: 400,
            maxWidth: 760,
            mx: 'auto',
            px: { xs: 2, md: 0 }
          }}
        >
          An independent inspection and survey agency serving exporters, importers and
          logistics operators at ports and facilities across India and around the world.
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, md: 4 }, 
                height: '100%', 
                transition: 'transform 0.2s',
                '&:hover': { 
                  transform: { md: 'translateY(-4px)' }
                }
              }}
            >
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  color: 'primary.main',
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}
              >
                Our Mission
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }}
              >
                To ensure operational excellence for our clients by integrating industry best practices 
                and maintaining high standards. We aim to be a trusted partner for all inspection needs, 
                empowering businesses with transparency, efficiency, and trustworthiness.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                Our Vision
              </Typography>
              <Typography paragraph>
                To revolutionize survey with impactful solutions that address real-world challenges.
                We are committed to building a robust foundation and assembling passionate teams 
                dedicated to turning ideas into reality.
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                Our Vision For Innovation
              </Typography>
              <Typography>
                Our upcoming project with lab testing facilities includes comprehensive analysis capabilities for:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Chemical analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Agro products analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Food products analysis" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Water, Mineral, coal, sugar analysing" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                Our Expertise
              </Typography>
              <Typography>
                We excel in warehouse operations, multimodal logistics management, vessel and cargo 
                assurance, safety compliance, and much more. At InspectIQ, quality truly meets trust.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Team Section */}
      <Box sx={{ 
          py: { xs: 6, md: 8 }, 
          px: { xs: 2, md: 0 },
          bgcolor: 'background.default' 
        }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            mb: { xs: 2, md: 3 }
          }}
        >
          Experienced & Dedicated Team
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          align="center" 
          sx={{ 
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            fontWeight: 400,
          }}
        >
          The people behind every inspection, audit and report we deliver
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                Experienced Leadership
              </Typography>
              <Typography paragraph>
                Our board of directors comprises seasoned professionals with deep industry knowledge. 
                Their expertise drives our success and ensures effective decision-making.
              </Typography>
              <List>
                {['Industry expertise', 'Strategic vision', 'Proven track record'].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                Dedicated Teams
              </Typography>
              <Typography paragraph>
                Our employees are our greatest asset. We meticulously evaluate their skills and assign 
                responsibilities accordingly. Regular training and grooming sessions keep our teams sharp 
                and adaptable.
              </Typography>
              <List>
                {['Skilled professionals', 'Continuous training', 'Client-focused approach'].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Director Profile Section */}
      <Box sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 0 },
      }}>
        <Typography
          variant="h3"
          gutterBottom
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            mb: { xs: 2, md: 3 },
          }}
        >
          Director Profile
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            maxWidth: 900,
            mx: 'auto',
            mt: { xs: 3, md: 4 },
          }}
        >
          {/* Profile Header */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: { xs: 2, sm: 3 },
            mb: 4,
          }}>
            <Avatar
              src={NileshGuptaImg}
              alt="Nilesh Gupta"
              sx={{
                width: { xs: 120, sm: 140, md: 160 },
                height: { xs: 120, sm: 140, md: 160 },
                border: '4px solid',
                borderColor: 'primary.main',
                borderRadius: '50% !important',
                '& .MuiAvatar-img': {
                  objectFit: 'cover',
                  objectPosition: '50% 15%',
                  pointerEvents: 'none',
                  userSelect: 'none',
                },
              }}
              imgProps={{ draggable: false }}
            />
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Nilesh Gupta
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.15rem' } }}>
                Director : InspectIQ
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Educational Qualification */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <SchoolIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Educational Qualification
              </Typography>
            </Box>
            <List dense disablePadding>
              {[
                'Bachelor of Science (B.Sc.) in Chemistry',
                'Master of Science (M.Sc.) in Organic Chemistry',
                'Post Graduate Diploma in Marketing Management (PGDMM)',
              ].map((item, idx) => (
                <ListItem key={idx} sx={{ py: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Professional Certification */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <VerifiedUserIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Professional Certification
              </Typography>
            </Box>
            <Chip
              label="Certified ISO Auditor"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.95rem', py: 2.5, px: 1 }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Experience */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <WorkHistoryIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Experience
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
              With over 11 years of experience in Quality Assurance (QA) and Quality Control (QC),
              Nilesh Gupta has developed strong expertise in the chemical and packaging sectors.
              He has hands-on experience in inspection, auditing, process improvement, and supplier
              quality management.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Core Expertise */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ color: 'primary.main', mb: 1.5, fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
              Core Expertise
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'Third-party Inspection & Audit',
                'Packaging Quality Control',
                'Chemical Industry QA/QC',
                'Supplier & Vendor Evaluation',
                'Compliance & ISO Standards',
              ].map((item, idx) => (
                <Chip
                  key={idx}
                  label={item}
                  color="primary"
                  size="medium"
                  sx={{ fontSize: '0.85rem' }}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Vision */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <VisibilityIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Vision
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
              To make "Inspection Made in India" a global benchmark by delivering reliable,
              high-quality inspection services and supporting SMEs (Small and Medium Enterprises)
              with complete inspection, compliance, and quality solutions.
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Mission */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <FlagIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Mission
              </Typography>
            </Box>
            <List dense disablePadding>
              {[
                'To provide trusted and transparent inspection services across industries',
                'To empower businesses with quality assurance and compliance support',
                'To build long-term partnerships with clients globally',
              ].map((item, idx) => (
                <ListItem key={idx} sx={{ py: 0.3 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Message from Director */}
          <Box sx={{
            bgcolor: 'rgba(1, 86, 122, 0.04)',
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            p: { xs: 2, md: 3 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <FormatQuoteIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" sx={{ color: 'primary.main', fontSize: { xs: '1.15rem', md: '1.35rem' } }}>
                Message from Director
              </Typography>
            </Box>
            <Typography sx={{ fontStyle: 'italic', fontSize: { xs: '0.9rem', md: '1rem' } }}>
              "At InspectIQ, we believe quality is not just a requirement, it is a commitment.
              Our goal is to support businesses at every stage with reliable inspection services
              and help them grow with confidence in global markets."
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Values Section */}
      <Box sx={{ 
          py: { xs: 6, md: 8 }, 
          px: { xs: 2, md: 0 },
          bgcolor: 'background.paper' 
        }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          align="center" 
          sx={{ 
            mb: { xs: 2, md: 3 },
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
          }}
        >
          Our Core Values
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          align="center" 
          sx={{ 
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
          }}
        >
          The principles that guide us in delivering excellence
        </Typography>
        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                {value.icon}
                <Typography variant="h5" gutterBottom sx={{ mt: 2, color: 'primary.main' }}>
                  {value.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {value.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default About;